/**
 * Modal that runs the user-side email-code login (verification-api → auth-api).
 *
 * When `targetUser` is provided, the modal header shows a hint.
 * The operator types the email manually because it is encrypted at rest.
 *
 * On success the resulting access_token is stored in DmPersonaContext.
 *
 * Props:
 *   open:        boolean
 *   targetUser:  { userId?: number, username?: string, ...userFields } | null
 *   onClose:     () => void
 *   onSuccess:   (persona: { userId, username, accessToken }) => void
 */
import React, { useEffect, useMemo, useState } from 'react';
import { X, MessageSquare, AlertTriangle } from 'lucide-react';
import { t } from '../../utils/i18n';
import { useDmPersona } from '../../context/DmPersonaContext';
import { requestEmailCode, verifyEmailCode } from '../../api/dmVerification';
import { dmUserLogin, dmUserSignup } from '../../api/dmAuth';
import { ApiError } from '../../api/dmApiClient';
import { getClientId } from '../../api/clientId';
import UserAvatar from '../UserAvatar';
import { displayUserEmail } from '../../utils/format';

const CODE_VALID_SECONDS = 5 * 60;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// verification-api verifyEmailCode returns:
//   data.status === 200 → no auths_beta row → branch to /auth/signup
//   data.status === 201 → existing user      → branch to /auth/login
const STATUS_EXISTING_USER = 201;

/**
 * Returns a localized error key for ApiErrors, or null for non-ApiErrors
 * (caller should display the raw message instead).
 */
function mapErrorToKey(err) {
  if (!(err instanceof ApiError)) return null;
  if (err.status === 0) return 'dm.userLoginModal.errors.network';
  switch (err.code) {
    case -401:
      return err.data?.data?.retry >= 5
        ? 'dm.userLoginModal.errors.tooManyAttempts'
        : 'dm.userLoginModal.errors.codeInvalid';
    case -410:
      return 'dm.userLoginModal.errors.codeExpired';
    case -404:
      return 'dm.userLoginModal.errors.verificationNotFound';
    case -1:
      return 'dm.userLoginModal.errors.emailInvalid';
    default:
      return 'dm.userLoginModal.errors.unknown';
  }
}

function getErrorMessage(err) {
  const key = mapErrorToKey(err);
  if (key) return t(key);
  // Non-ApiError: surface the raw message so the operator can diagnose
  return err?.message || t('dm.userLoginModal.errors.unknown');
}

function checkMissingEnv() {
  // HMAC signing is now server-side (BFF). No secret key needed in the browser.
  // Proxy paths are hardcoded in dmApiClient.js, so no env vars to check here.
  return [];
}

export default function DmUserLoginModal({ open, targetUser, onClose, onSuccess }) {
  const { login: setPersona } = useDmPersona();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [bannerError, setBannerError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setEmail('');
    setCode('');
    setVerificationId(null);
    setCodeExpiresAt(null);
    setEmailError(null);
    setCodeError(null);
    setBannerError(null);
    setLoading(false);
  }, [open, targetUser?.userId]);

  useEffect(() => {
    if (!open || step !== 2 || codeExpiresAt == null) {
      setRemainingSeconds(null);
      return;
    }
    const tick = () => setRemainingSeconds(Math.max(0, Math.floor((codeExpiresAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [open, step, codeExpiresAt]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const formattedRemaining = useMemo(() => {
    if (remainingSeconds == null) return null;
    const m = Math.floor(remainingSeconds / 60);
    const s = String(remainingSeconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  }, [remainingSeconds]);

  if (!open) return null;

  const targetLabel = (() => {
    if (!targetUser) return null;
    const uid = targetUser.userId ?? targetUser.user_id;
    const uname = targetUser.username || '';
    if (uid && uname) return `#${uid} · ${uname}`;
    if (uid) return `#${uid}`;
    if (uname) return uname;
    return null;
  })();

  const handleStep1 = async (e) => {
    e.preventDefault();
    setEmailError(null);
    setBannerError(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailError(t('dm.userLoginModal.errors.emailRequired'));
      return;
    }
    if (!EMAIL_RE.test(trimmed)) {
      setEmailError(t('dm.userLoginModal.errors.emailInvalid'));
      return;
    }
    const missing = checkMissingEnv();
    if (missing.length > 0) {
      setBannerError(`Missing env vars in .env: ${missing.join(', ')}`);
      return;
    }
    setLoading(true);
    try {
      const { verificationId: vid } = await requestEmailCode(trimmed);
      setVerificationId(vid);
      setEmail(trimmed);
      setCode('');
      setCodeExpiresAt(Date.now() + CODE_VALID_SECONDS * 1000);
      setStep(2);
    } catch (err) {
      setBannerError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setCodeError(null);
    setBannerError(null);
    const normalized = code.replace(/\s/g, '');
    if (!/^\d{6}$/.test(normalized)) {
      setCodeError(t('dm.userLoginModal.errors.codeRequired'));
      return;
    }
    setLoading(true);
    try {
      const verifyRes = await verifyEmailCode(verificationId, normalized);
      const status = verifyRes.status;
      const tokenRes =
        status === STATUS_EXISTING_USER
          ? await dmUserLogin(email, verificationId)
          : await dmUserSignup(email, verificationId);
      if (!tokenRes.accessToken || !tokenRes.userId) {
        throw new ApiError('No access_token in auth response', { status: 0 });
      }
      setPersona(tokenRes.accessToken, {
        userId: tokenRes.userId,
        username: tokenRes.username,
        deviceId: getClientId(),
      });
      onSuccess?.({
        userId: tokenRes.userId,
        username: tokenRes.username,
        accessToken: tokenRes.accessToken,
      });
    } catch (err) {
      const msg = getErrorMessage(err);
      setCodeError(msg);
      setBannerError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setBannerError(null);
    try {
      const { verificationId: vid } = await requestEmailCode(email);
      setVerificationId(vid);
      setCode('');
      setCodeExpiresAt(Date.now() + CODE_VALID_SECONDS * 1000);
    } catch (err) {
      setBannerError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setCode('');
    setCodeError(null);
    setBannerError(null);
    setCodeExpiresAt(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dm-user-login-modal-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-dark-card border border-dark-border rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-5 py-4 border-b border-dark-border">
          <div className="flex items-center gap-2">
            {targetUser ? <UserAvatar user={targetUser} size={36} /> : (
              <span className="w-9 h-9 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center">
                <MessageSquare size={18} className="text-primary" />
              </span>
            )}
            <div>
              <h2 id="dm-user-login-modal-title" className="text-base font-semibold text-dark-text">
                {t('dm.userLoginModal.title')}
              </h2>
              <p className="text-xs text-dark-text-secondary">
                {targetLabel
                  ? t('dm.userLoginModal.targetHint', { target: targetLabel })
                  : t('dm.userLoginModal.subtitle')}
              </p>
              {targetUser ? (
                <p className="text-[11px] text-primary truncate">{displayUserEmail(targetUser)}</p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-dark-text-secondary hover:text-primary"
            aria-label={t('common.cancel')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-primary/5 border border-primary/20 text-primary text-xs leading-snug">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{t('dm.userLoginModal.warning')}</span>
          </div>

          {checkMissingEnv().length > 0 && (
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-xs space-y-1">
              <p className="font-semibold">{t('dm.userLoginModal.errors.missingEnv')}</p>
              {checkMissingEnv().map((key) => (
                <code key={key} className="block text-yellow-200 font-mono">{key}</code>
              ))}
            </div>
          )}

          {bannerError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm break-all">
              {bannerError}
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleStep1} className="space-y-3">
              <label className="block">
                <span className="text-sm text-dark-text-secondary">{t('dm.userLoginModal.step1.email')}</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
                  placeholder={t('dm.userLoginModal.step1.emailPlaceholder')}
                  className={`mt-1 w-full px-3 py-2 rounded-md bg-dark-surface border text-dark-text placeholder:text-dark-text-muted focus:outline-none focus:border-primary transition-colors ${
                    emailError ? 'border-red-500/60' : 'border-dark-border'
                  }`}
                  autoComplete="email"
                  autoFocus
                  aria-invalid={!!emailError}
                />
                {emailError && <span className="block mt-1 text-xs text-red-400">{emailError}</span>}
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 rounded-md bg-primary text-black font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {loading ? t('common.loading') : t('dm.userLoginModal.step1.submit')}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleStep2} className="space-y-3">
              <p className="text-xs text-dark-text-secondary">
                {t('dm.userLoginModal.step2.description', { email })}
              </p>
              <p className="text-xs" aria-live="polite">
                {remainingSeconds != null && remainingSeconds > 0 ? (
                  <span className="text-primary">
                    {t('dm.userLoginModal.step2.timerRemaining', { time: formattedRemaining })}
                  </span>
                ) : remainingSeconds === 0 ? (
                  <span className="text-red-400">{t('dm.userLoginModal.step2.timerExpired')}</span>
                ) : null}
              </p>

              <label className="block">
                <span className="text-sm text-dark-text-secondary">{t('dm.userLoginModal.step2.title')}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setCodeError(null); }}
                  placeholder={t('dm.userLoginModal.step2.codePlaceholder')}
                  className={`mt-1 w-full px-3 py-2 rounded-md bg-dark-surface border text-dark-text text-center tracking-[0.3em] text-lg focus:outline-none focus:border-primary transition-colors ${
                    codeError ? 'border-red-500/60' : 'border-dark-border'
                  }`}
                  autoFocus
                  aria-invalid={!!codeError}
                />
                {codeError && <span className="block mt-1 text-xs text-red-400">{codeError}</span>}
              </label>

              <div className="flex gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-md border border-dark-border text-dark-text-secondary hover:text-primary hover:border-primary transition-colors"
                  onClick={handleBack}
                  disabled={loading}
                >
                  {t('dm.userLoginModal.step2.back')}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-md border border-dark-border text-dark-text-secondary hover:text-primary hover:border-primary transition-colors"
                  onClick={handleResend}
                  disabled={loading}
                >
                  {t('dm.userLoginModal.step2.resend')}
                </button>
                <button
                  type="submit"
                  disabled={loading || remainingSeconds === 0}
                  className="flex-1 py-2 rounded-md bg-primary text-black font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {loading ? t('common.loading') : t('dm.userLoginModal.step2.submit')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
